import React, { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../contexts/DashboardContext";
import firebase from "../../firebase";
import moment from "moment";
import { Code } from "components/layout/Code";
import Modal from "./Modal";
import { PhotoBoothSession, Code as CodeType } from "@mijnfotohoek-types";
import { TableRow } from "components/layout/TableRow";
import SearchBar from "components/forms/SearchBar";
import Select from "components/forms/Select";
import { Skeleton } from "@chakra-ui/react";
import Button from "components/forms/Button";
import { FaSpinner, FaUpload } from "react-icons/fa";
import ModalPhotoBoothSessionUpload from "./ModalPhotoBoothSessionUpload";

const ModalPhotoBoothSession = ({
  photoBoothSession,
  close,
}: {
  photoBoothSession: PhotoBoothSession;
  close: () => void;
}) => {
  const { user } = useContext(DashboardContext);
  const [data, setData] = useState<CodeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadPercentage, setDownloadPercentage] = useState(null);
  useEffect(() => {
    if (user != null) {
      const unsubscribe = firebase
        .firestore()
        .collection("codes")
        .where("user", "==", user)
        .where("photoBoothSession", "==", photoBoothSession.id)
        .onSnapshot((snapshot) => {
          const docs = snapshot.docs.map((doc) => {
            return {
              id: doc.id,
              ...doc.data(),
            };
          });
          setData(docs as CodeType[]);
          setLoading(false);
        });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    setDownloadPercentage(getDownloadPercentage());
  }, [data]);

  const getDownloadPercentage = () => {
    const active_codes = data.filter((d) => d.photos && d.photos.length != 0);
    const active_codes_length = active_codes.length;
    if (active_codes_length === 0) return 0;
    const downloaded = active_codes.filter((a) => a.downloads.length != 0);
    return Math.round((downloaded.length / active_codes_length) * 100);
  };

  const [filter, setFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const [modalUpload, setModalUpload] = React.useState(false);

  const loadingSkeletons: JSX.Element[] = Array.from(
    { length: 4 },
    // @ts-ignore
    (_, index) => <Skeleton key={index} height="95px" borderRadius="16px" />,
  );

  const getFilteredCodes = () => {
    if (loading) return [];
    return data
      .filter((d) => {
        const normalizedFilter = filter.trim().toUpperCase();
        return (
          normalizedFilter.length === 0 ||
          d.id.substring(0, normalizedFilter.length) === normalizedFilter
        );
      })
      .filter((d) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "unused") return d.downloads.length === 0;
        if (statusFilter === "used") return d.downloads.length > 0;
        return true;
      })
      .sort(
        (a, b) => Number(a.photos.length === 0) - Number(b.photos.length === 0),
      );
  };

  return (
    <Modal close={close} title={photoBoothSession.title}>
      {modalUpload && (
        <ModalPhotoBoothSessionUpload
          photoBoothSession={photoBoothSession}
          codes={data}
          close={() => setModalUpload(false)}
        />
      )}

      <div className="flex flex-col gap-4 my-4">
        <table className="table-auto w-full text-sm md:text-base">
          <tbody>
            <TableRow
              rowKey="Aangemaakt"
              toolTip="Dag en tijdstip waarop deze fotohoek is aangemaakt"
            >
              <p>
                {moment(photoBoothSession.createdAt.toDate()).format(
                  "dddd DD MMMM YYYY HH:mm",
                )}
              </p>
            </TableRow>
            <TableRow
              rowKey="Verloopt"
              toolTip="Dag en tijdstip waarop deze fotohoek verloopt"
            >
              <p className="">
                {moment(photoBoothSession.expires).format("dddd DD MMMM YYYY")}
              </p>
            </TableRow>
            <TableRow
              rowKey="Downloadpercentage"
              toolTip="Het percentage van QR-codes dat is gedownload (alleen voor QR-codes met foto's)"
            >
              <p className="">
                {downloadPercentage !== null ? (
                  `${downloadPercentage}%`
                ) : (
                  <FaSpinner className="animate-spin text-gray-500" />
                )}
              </p>
            </TableRow>
            <TableRow
              rowKey="Aantal QR-codes"
              toolTip="Het aantal QR-codes dat aan deze fotohoek is gekoppeld"
            >
              <p>{photoBoothSession.qrCodesCount}</p>
            </TableRow>

            {/* <TableRow
              rowKey="Gedownload"
              toolTip="Het percentage van QR-codes dat is gedownload (alleen voor QR-codes met foto's)"
            >
              <p>75%</p>
            </TableRow> */}
          </tbody>
        </table>
        <div className="flex justify-between  mt-4">
          <h1 className="font-bold text-2xl">QR-codes </h1>
          <Button isButtonIcon={true} onClick={() => setModalUpload(true)}>
            <FaUpload />
          </Button>
        </div>
        <div className="grid grid-cols-[1fr,200px] gap-2">
          <SearchBar
            setFilter={setFilter}
            value={filter || ""}
            placeholder="Zoek een code"
          />
          <Select
            options={[
              { label: "Alles", value: "all" },
              { label: "Ongebruikt", value: "unused" },
              { label: "Gebruikt", value: "used" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
        {loading && <>{loadingSkeletons}</>}
        {getFilteredCodes().map((d) => (
          <Code key={d.id} code={d} />
        ))}
      </div>
    </Modal>
  );
};

export default ModalPhotoBoothSession;
